import React, { useState } from "react";
import { Download, FileText, FileSpreadsheet, File, Loader2, CheckCircle2, XCircle } from "lucide-react";

// NOTE: fetches use relative "/api/..." URLs — see scripts/check_fetch_style.sh

const REPORTS = [
  {
    id: "PHASE3_FINAL_REPORT",
    title: "Phase 3 Final Validation Report",
    description: "Board-ready validation report with executive summary, methodology, key results, and acceptance status.",
    formats: [
      { fmt: "pdf",  label: "PDF",   icon: File },
      { fmt: "docx", label: "Word",  icon: FileSpreadsheet },
      { fmt: "md",   label: "Markdown", icon: FileText },
    ],
  },
  {
    id: "PHASE3_FINAL_EVIDENCE",
    title: "Phase 3 Final Evidence (Structured)",
    description: "Complete structured evidence with all 24 test cases, scores, deltas, guards, and intent flags.",
    formats: [
      { fmt: "json", label: "JSON", icon: FileText },
    ],
  },
  {
    id: "phase3_validation_run2",
    title: "Validation Run #2 Evidence",
    description: "Detailed per-case data from the second validation run including repeatability results.",
    formats: [
      { fmt: "json", label: "JSON", icon: FileText },
    ],
  },
];

function DownloadButton({ reportId, fmt, label, Icon }) {
  const [state, setState] = useState("idle");

  const handleDownload = async () => {
    setState("loading");
    try {
      const res = await fetch(`/api/admin/reports/download/${reportId}/${fmt}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportId}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setState("done");
      setTimeout(() => setState("idle"), 2000);
    } catch (e) {
      console.error("Download failed:", e);
      setState("error");
      setTimeout(() => setState("idle"), 3000);
    }
  };

  return (
    <button
      data-testid={`download-${reportId}-${fmt}`}
      onClick={handleDownload}
      disabled={state === "loading"}
      className={`
        inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium
        transition-all duration-200
        ${state === "done"
          ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
          : state === "error"
          ? "bg-red-500/20 text-red-400 border border-red-500/30"
          : "bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10 hover:border-white/20 hover:text-white"
        }
        disabled:opacity-50
      `}
    >
      {state === "loading" ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : state === "done" ? (
        <CheckCircle2 className="w-4 h-4" />
      ) : state === "error" ? (
        <XCircle className="w-4 h-4" />
      ) : (
        <Icon className="w-4 h-4" />
      )}
      {state === "loading" ? "Downloading..." : state === "done" ? "Downloaded" : state === "error" ? "Failed" : label}
    </button>
  );
}

export default function DownloadsPage() {
  return (
    <div className="min-h-screen bg-[#0a0e1a]">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <Download className="w-7 h-7 text-blue-400" />
            <h1 className="text-2xl font-bold text-white" data-testid="downloads-title">
              Phase 3 Deliverables
            </h1>
          </div>
          <p className="text-gray-400 text-sm">
            Download validation reports and structured evidence files.
          </p>
        </div>

        <div className="space-y-6">
          {REPORTS.map((report) => (
            <div
              key={report.id}
              data-testid={`report-card-${report.id}`}
              className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6"
            >
              <h2 className="text-lg font-semibold text-white mb-1">
                {report.title}
              </h2>
              <p className="text-gray-500 text-sm mb-5">
                {report.description}
              </p>
              <div className="flex flex-wrap gap-3">
                {report.formats.map(({ fmt, label, icon }) => (
                  <DownloadButton
                    key={fmt}
                    reportId={report.id}
                    fmt={fmt}
                    label={label}
                    Icon={icon}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-white/[0.06]">
          <p className="text-gray-600 text-xs">
            Generated by MSML HEALTH Validation Engine v1.0.24 — All evidence is live-tested against production LLM endpoints.
          </p>
        </div>
      </div>
    </div>
  );
}
